import { useState, useEffect, useCallback, useRef } from 'react'
import type { RssArticle } from '../types/rss'
import { fetchAllRssFeeds } from '../utils/rss'

const CACHE_DURATION_MS = 5 * 60 * 1000

interface CacheEntry {
  data: RssArticle[]
  timestamp: number
}

let globalCache: CacheEntry | null = null

interface UseRssFeedsResult {
  articles: RssArticle[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export function useRssFeeds(): UseRssFeedsResult {
  const [articles, setArticles] = useState<RssArticle[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const isMounted = useRef(true)

  const fetchData = useCallback(async (force = false) => {
    const now = Date.now()

    if (!force && globalCache && now - globalCache.timestamp < CACHE_DURATION_MS) {
      setArticles(globalCache.data)
      setLoading(false)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const data = await fetchAllRssFeeds()

      if (!isMounted.current) return

      globalCache = { data, timestamp: now }
      setArticles(data)
    } catch (err) {
      if (!isMounted.current) return

      const message = err instanceof Error ? err.message : 'Failed to fetch RSS feeds'
      setError(message)
    } finally {
      if (isMounted.current) {
        setLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    isMounted.current = true
    fetchData()

    return () => {
      isMounted.current = false
    }
  }, [fetchData])

  const refetch = useCallback(async () => {
    await fetchData(true)
  }, [fetchData])

  return { articles, loading, error, refetch }
}
